<template>
  <zm-dialog
    v-model="visible"
    class="phone-number-select opc-full-zm-dialog"
    width="544px"
    :title="$t('billing.pbx.search_number_title')"
  >
    <!-- phone number select dialog with title -->
    <template #header>
      <div class="dialog-header">
        <h2>
          {{ $t('billing.pbx.search_number_title') }}
        </h2>
        <zm-tooltip class="dialog-header__tooltip" placement="bottom" :content="$t('billing.pbx.phone_number_guarantee_tips')">
          <tips-icon aria-label="select local or toll-free number" />
        </zm-tooltip>
      </div>
    </template>
    <!-- SMS warning alert - hidden for free trial -->
    <div class="alert-row" v-if="!isFreeTrialFlow">
      <zm-banner
        class="zbo-billing-warning sms-warning"
        type="warning"
      >
        <span>{{ $t('billing.additional_registration_needed_sms') }}&nbsp;</span>
        <zm-button
          ref="smsLearnMoreButton"
          class="zbo-billing-link-button"
          @click="openSmsLearnMoreDialog"
          type="text"
        >{{ $t('billing.common.Learn_More') }}</zm-button>
      </zm-banner>
    </div>
    <zm-tabs
      v-model="searchByPhoneType"
      class="phone-number-select__content"
      :class="{ 'phone-number-select__content--hide-header': isFreeTrialFlow }"
    >
      <!-- local number selection -->
      <zm-tab-pane
        :label="localNumberTabTitle"
        :name="phoneNumberTypeMap.local"
        class="phone-number-select__content--local">
        <local-search
          ref="localSearch"
          :check-exist-unread-phone-numbers="checkUnreadPhoneNumbers(phoneNumberRefsKeyMap.local)"
          :goto-next-page="gotoNextPage(phoneNumberRefsKeyMap.local)" />
        <!-- Free Trial info alert -->
        <div v-if="isFreeTrialFlow" class="phone-number-select__trial-alert">
          <zm-banner
            type="info"
          >
            <span v-html="freeTrialBannerInfo" @click="handleFreeTrialBannerClick"></span>
          </zm-banner>
        </div>
        <phone-number-form-list
          ref="localNumberList"
          :max-select-phone-numbers="maxSelectPhoneNumbers"
          :type="phoneNumberTypeMap.local"
          :loading="localNumber.loading"
          :port-number-count="portCount"
          :localNumberShowClear="localNumberShowClear"
          :phone-number-list="localNumber.phoneNumberList"
          :checked-phone-number-map="checkedPhoneNumberMap"
          :single-selection="isFreeTrialFlow"
          @checkedNumberList="handleCheckedNumberList" />
      </zm-tab-pane>
      <!-- toll free number selection - hidden for free trial -->
      <zm-tab-pane
        v-if="!isFreeTrialFlow"
        :label="tollFreeNumberTabTitle"
        :name="phoneNumberTypeMap.tollFree"
        class="phone-number-select__content--toll-free"
      >
        <div class="phone-number-select__toll-free-tips">
          {{ tollFreeSearchTinyTips }}
          <zm-tooltip
            placement="bottom-end"
            floating-content-class="phone-number-select__toll-free-tips--content"
            :content="$t('billing.pbx.toll_free.popover_tips')"
          >
            <tips-icon aria-label="toll-free numbers" />
          </zm-tooltip>
        </div>
        <toll-free-search
          ref="tollFreeSearch"
          :check-exist-unread-phone-numbers="checkUnreadPhoneNumbers(phoneNumberRefsKeyMap.tf)"
          :goto-next-page="gotoNextPage(phoneNumberRefsKeyMap.tf)"
        />
        <phone-number-form-list
          ref="tfNumberList"
          :max-select-phone-numbers="maxSelectPhoneNumbers"
          :type="phoneNumberTypeMap.tollFree"
          :port-number-count="portCount"
          :loading="tollFreeNumber.loading"
          :phone-number-list="tollFreeNumber.phoneNumberList"
          :checked-phone-number-map="checkedPhoneNumberMap"
          :empty-result-tips="tollFreeSearchEmptyResultTips"
          @checkedNumberList="handleCheckedNumberList"
        />
      </zm-tab-pane>
    </zm-tabs>
    <!-- have selected phone number list -->
    <phone-number-checked-list
      :checked-phone-number-list="checkedPhoneNumberList"
      :checked-local-number-list="checkedLocalNumberList"
      :localNumberShowClear="localNumberShowClear"
      :single-selection="isFreeTrialFlow"
      @removeNumber="handleRemoveNumber"
    />
    <template #footer>
      <phone-select-footer :disabled-save="disabledSave" :on-cancel="onCancel" :on-save="onSave"/>
    </template>
    <AdditionalSMSLearnMore v-model="additionalSmsLearnMoreVisible" />
  </zm-dialog>
</template>

<script>
import { filter, get, values, find, forEach } from 'lodash';
import { AdditionalSMSLearnMore } from '@zoom/fe-billing-components';
import LocalSearch from '@/plans/ZOOM_PHONE/component/local-search';
import PhoneNumberFormList from '@/plans/ZOOM_PHONE/component/phone-number-form-list';
import TollFreeSearch from '@/plans/ZOOM_PHONE/component/toll-free-search';
import PhoneNumberCheckedList from '@/plans/ZOOM_PHONE/component/phone-number-checked-list';
import { ConfirmComponent } from '@/components/common/service-dialog/confirm';
import { localNumberStore, tollFreeNumberStore } from '../../store';
import { maxSelectPhoneNumbers, PBX_REGIONS, phoneNumberTypeMap } from '@/plans/ZOOM_PHONE/helper/constants';
import computedMixin from '@/plans/ZOOM_PHONE/mixin/computed';
import { BILLING_CYCLE, STAND_ALONE_PLAN_TYPES } from '@/common/constants';
import PhoneSelectFooter from "./phone-select-footer";
import { getCurrentTrackingPlanName } from '@/plans/ZOOM_PHONE/helper/utils';
import { isEmptyVal } from '@/utils';
import { mapGetters } from 'vuex';
import { logger } from '@/utils/log';

export default {
  name: 'PhoneNumberSelectDialog',
  mixins: [ConfirmComponent, computedMixin],
  components: {
    PhoneNumberCheckedList,
    PhoneNumberFormList,
    LocalSearch,
    TollFreeSearch,
    PhoneSelectFooter,
    AdditionalSMSLearnMore,
  },
  data() {
    return {
      searchByPhoneType: phoneNumberTypeMap.local,
      phoneNumberTypeMap,
      localNumber: localNumberStore.state,
      tollFreeNumber: tollFreeNumberStore.state,
      checkedPhoneNumberMap: {},
      // true means phone number not required check
      // false means phone number should required check
      localCount: 0,
      tollFreeCount: 0,
      portCount: 0,
      clearable: true,
      phoneNumberRefsKeyMap: Object.freeze({
        local: 'localNumberList',
        tf: 'tfNumberList'
      }),
      maxSelectPhoneNumbers: maxSelectPhoneNumbers,
      additionalSmsLearnMoreVisible: false,
    };
  },
  computed: {
    ...mapGetters(['boughtPlans']),
    isFreeTrialFlow() {
      return this.$context?.is?.buyTrial || this.$context.is.add2trial;
    },
    trackingPlanName() {
      return getCurrentTrackingPlanName();
    },
    // Check if the account has already purchased a meeting plan
    hasMeetingBasePlan() {
      return this.getBoughtMeetingPlans && this.getBoughtMeetingPlans.length > 0;
    },
    // Check if is standalone plan account (ZOOM_PHONE, ZOOM_ROOMS, ZOOM_WHITEBOARD, etc.)
    isStandalonePlanAccount() {
      return this.getBoughtBasePlanInfo &&
        STAND_ALONE_PLAN_TYPES.includes(this.getBoughtBasePlanInfo.planType);
    },
    // Generate the appropriate purchase link based on account type
    freeTrialBannerInfo() {
      const purchaseLink = this.generatePurchaseLink();
      return this.$t('billing.pbx.free_trial.single_number_limit_info_template', [purchaseLink]);
    },
    checkedPhoneNumberList() {
      return values(this.checkedPhoneNumberMap) || [];
    },
    checkedLocalNumberList() {
      return filter(this.checkedPhoneNumberList, (item) => item.type === phoneNumberTypeMap.local);
    },
    checkedTollFreeNumberList() {
      return filter(this.checkedPhoneNumberList, (item) => item.type === phoneNumberTypeMap.tollFree);
    },
    localNumberTabTitle() {
      const count = this.checkedLocalNumberList?.length;
      return this.$t('billing.pbx.local.title') + `${count ? ` (${count})` : ''}`;
    },
    tollFreeNumberTabTitle() {
      const count = this.checkedTollFreeNumberList?.length;
      return this.$t('billing.pbx.toll_free.title') + `${count ? ` (${count})` : ''}`;
    },
    checkedLocalNumberCount() {
      return this.checkedLocalNumberList?.length || 0;
    },
    localNumberShowClear() {
      // allow clear means can clear all phone numbers
      // For free trial: clearable=false (passed from buy-trial.vue), so returns false
      // For regular flow: clearable=true (default), returns true unless only 1 number is selected
      return this.clearable ? true : (this.checkedLocalNumberCount === 1 ? false : true);
    },
    disabledSave() {
      // not allowed to clear phone number and phone number not checked
      // in other words, should required phone number, but phone number not selected
      return !this.clearable && !this.checkedPhoneNumberList?.length;
    },
    tollFreeSearchTinyTips() {
      const i18mKey = this.pbxBillingCycle === BILLING_CYCLE.ANNUAL ?
        'billing.pbx.toll_free.search_tips.annual' :
        'billing.pbx.toll_free.search_tips.monthly';

      const currentTollFreePlans = find(this.allTollFreePlans, (p) => {
        return p.billing_cycle === this.pbxBillingCycle && p.region === PBX_REGIONS.US_AND_CANADA;
      });

      return this.$t(i18mKey, [this.$money(get(currentTollFreePlans, 'amount'))]);
    },
    tollFreeSearchEmptyResultTips() {
      const { searchForm, searchTimes } = tollFreeNumberStore.state || {};
      const { selectTfPrefix, vanitySuffix } = searchForm || {};
      if (isEmptyVal(selectTfPrefix) && isEmptyVal(vanitySuffix) || searchTimes === 0) {
        return this.$t('billing.pbx.tollFree.empty_tips_for_no_search')
      }

      return this.$t('billing.pbx.no_results');
    }
  },
  methods: {
    onSave() {
      this.$tracking.buyInteraction(`${this.trackingPlanName} numbers`, `${this.callingPlanName} - pick your numbers - local and toll-free  - save`);
      return this.handleSure({ checkedPhoneNumberMap: this.checkedPhoneNumberMap });
    },
    onCancel() {
      this.$tracking.buyInteraction(`${this.trackingPlanName} numbers`, `${this.callingPlanName} - pick your numbers - local and toll-free - cancel`);
      return this.handleCancel('close select phone number dialog').catch((error) => {
        logger.warn('cancel select phone number dialog error or close dialog, error: ', error);
      });
    },
    /**
     * Generate purchase link based on account type
     * Common params: period=annual, quantity=1, usageType=business, from=pbx_ZVU
     */
    generatePurchaseLink() {
      // Common query parameters
      const commonParams = {
        period: 'annual',
        quantity: '1',
        usageType: 'business',
        from: 'pbx_ZVU'
      };

      // If account has bought meeting base plan || If standalone plan account (e.g., ZOOM_PHONE, ZOOM_ROOMS, ZOOM_WHITEBOARD)
      if (this.hasMeetingBasePlan || this.isStandalonePlanAccount || this.$context.is.add2trial) {
        return this.buildUrl('/opc/buy', {
          ...commonParams,
          plan: 'pbx',
          subPlan: 'newsubs',
          from_sub: 'pbx',
          type: 'add',
          majorPlanHost: '1'
        });
      }

      // If buy now flow (NN - Net New user)
      if (!this.$context.is.loggedIn && this.$context?.is?.buyTrial) {
        return this.buildUrl('/opc/buy', {
          ...commonParams,
          plan: 'pbx',
          addon_period: 'annual'
        });
      }

      // If existing account but hasn't bought anything (basic2pro flow)
      if (this.$context.is.loggedIn && this.$context?.is?.buyTrial && this.boughtPlans.length === 0) {
        return this.buildUrl('/opc/buy/config', {
          ...commonParams,
          plan: 'pbx',
          subPlan: 'allSubPlan',
          type: 'basic2pro'
        });
      }

      // Other cases - redirect to pricing page
      return '/pricing';
    },
    /**
     * Build URL with query parameters
     * @param {string} basePath - base URL path
     * @param {Object} params - query parameters
     * @returns {string} complete URL with query string
     */
    buildUrl(basePath, params) {
      const queryString = Object.entries(params)
        .map(([key, value]) => `${key}=${value}`)
        .join('&');
      return `${basePath}?${queryString}`;
    },
    handleCheckedNumberList(val) {
      this.checkedPhoneNumberMap = val;
    },
    handleRemoveNumber(phoneNumber) {
      delete this.checkedPhoneNumberMap[phoneNumber];
    },
    resetSearchForm() {
      forEach([this.$refs?.localSearch, this.$refs?.tollFreeSearch], (formInstance) => {
        formInstance?.resetForm();
      })
    },
    checkUnreadPhoneNumbers(refKey) {
      return () => {
        return this.$refs?.[refKey]?.checkExistUnreadPhoneNumbers();
      }
    },
    gotoNextPage(refKey) {
      return () => {
        return this.$refs?.[refKey]?.gotoNextPage();
      };
    },
    openSmsLearnMoreDialog() {
      this.additionalSmsLearnMoreVisible = true;
    },
    trackFreeTrialBannerImpression() {
      // Track banner impression: Product Name - Banner Description - impression
      this.$tracking.buyInteraction(
        'banner - product level',
        `zoom phone free trial - you can only edit 1 number at this time - impression`
      );
    },
    handleFreeTrialBannerClick(event) {
      // Check if the clicked element is a link
      if (event.target && event.target.nodeName === 'A') {
        const linkText = event.target.innerText || event.target.textContent;
        // Track link click: Product Name - Banner Description - Link Text
        this.$tracking.buyInteraction(
          'banner - product level',
          `zoom phone free trial - you can only edit 1 number at this time - ${linkText}`
        );
      }
    },
  },
  watch: {
    visible: {
      handler(val) {
        !val && this.resetSearchForm();
        // Track banner impression for free trial flow
        if (val && this.isFreeTrialFlow) {
          this.trackFreeTrialBannerImpression();
        }
      },
    },
    additionalSmsLearnMoreVisible(val, oldVal) {
      // When dialog closes (changes from true to false), refocus the trigger button
      if (oldVal && !val && this.$refs.smsLearnMoreButton) {
        this.$nextTick(() => {
          this.$refs.smsLearnMoreButton.$el?.focus();
        });
      }
    }
  }
};
</script>

<style lang="scss">
@import '@/styles/_variables.scss';
@import '@zoom/billing-responsive/styles/responsive.scss';
@import '@/styles/scss-utils.scss';

.phone-number-select {
  @include media(desktop, tablet) {
    min-height: 500px;
  }

  @include media(mobile) {
    overflow-y: hidden;
  }

  &__toll-free-tips {
    font-size: 12px;
    margin-bottom: 10px;

    &--content {
      width: 290px;
    }
  }

  &__content {
    .zbo-tabs {
      &__nav {
        .zbo-tabs__active-bar {
          display: none;
        }
        &-wrap {
          margin-bottom: 12px;
          &::after {
            display: none;
          }
        }
      }
      &__item {
        margin-right: 12px;
        border-radius: 999px;
        padding: 5px 15px;
        border: 1px solid var(--zoom-color-border-input, #C1C6CE);
        font-size: 16px;
        line-height: 19px;

        &.is-active {
          color: var(--zoom-color-text-primary, #0D6BDE);
          background: var(--zoom-color-toggle-button-background-selected, #ECF4FD);
          border: 1px solid var(--zoom-color-fill-primary, #0D6BDE);
        }
        &::after {
          display: none;
        }
      }
    }

    // Hide tab header for free trial (single tab)
    &--hide-header {
      .zbo-tabs__nav-wrap {
        display: none;
      }
    }
  }

  &__trial-alert {
    margin: 16px 0;
    line-height: 18px;

    a {
      color: #0B5CFF;
      text-decoration: none;
      font-weight: 500;

      &:hover {
        text-decoration: underline;
      }
    }
  }

  &__footer {
    padding-top: 24px;
    display: flex;
    align-items: center;
    justify-content: flex-end;
    border-top: 1px solid var(--zoom-color-border-subtle-neutral, #DFE3E8);
  }

  @include media(mobile) {
    .zbo-dialog {
      &__header, &__body, &__footer {
        padding-left: 12px;
        padding-right: 12px;
      }
      &__body {
        scroll-behavior: smooth;
      }

      &__footer {
        padding-top: 12px;

        .phone-number-select {
          &__footer {
            padding-top: 12px;
            display: flex;
            justify-content: center;

            .zbo-button {
              flex: 1;
            }
          }
        }
      }
    }
  }
}
.alert-row {
  margin-bottom: 16px;
}
</style>

<style lang="scss" scoped>
.dialog-header {
  display: flex;
  &__tooltip {
    margin-top: 3px;
  }
}
</style>
